import React, { useCallback, useEffect, useState } from "react";

// One gentle shot of confetti on mount, then it settles and removes itself.
// Nothing loops and nothing pulses -- this is a celebration, not a slot machine.
// Skipped entirely for anyone who has asked for reduced motion.

interface Props {
  // Gold shades read as champagne against dark plum; the deeper golds are
  // needed on a near-white page or the pale ones wash out.
  colours: string[];
  // How far a piece falls, as a fraction of the height of the section it sits
  // in. Deliberately NOT viewport units: the section it lives in is clipped, so
  // a fall measured against the viewport is longer than the section on any page
  // whose hero is short, and pieces vanish at the clip line mid-flight instead
  // of completing their fall.
  travel?: [number, number];
  // Seconds a piece takes to fall, min and max. This sets the whole mood: a
  // long fall drifts and reads as ceremony, a short one reads as a party
  // popper. The learner's page wants the latter -- they have just passed
  // something -- while the public verification page is a document and can
  // afford to be statelier.
  duration?: [number, number];
  // Pieces at desktop width; mobile gets the same figure scaled down, since a
  // narrow screen fills up much faster.
  pieces?: number;
}

// How long a piece can be alive: the longest fall plus the longest stagger, in
// milliseconds, with a margin. After this the batch is taken off the DOM.
const lifetimeMs = (maxDuration: number, maxDelay: number) =>
  Math.round((maxDuration + maxDelay) * 1000) + 400;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

interface Piece {
  key: number;
  round: boolean;
  background: string;
  left: string;
  "--delay": string;
  "--dur": string;
  // A unitless fraction, not a length. The distance is worked out in CSS
  // against --region-h, which is kept up to date below, because the region
  // resizes underneath the animation: the certificate frame is a placeholder
  // ratio until the image loads and then snaps to the artwork's real shape,
  // which on the learner's page takes about 150px out of the region. Baking a
  // pixel distance in at mount meant pieces sized to the taller placeholder
  // and then overshot the frame once it shrank.
  "--travel-f": string;
  "--spin": string;
}

const Confetti: React.FC<Props> = ({
  colours,
  travel = [0.55, 0.95],
  duration = [3, 5.2],
  pieces: pieceCount = 52,
}) => {
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [spent, setSpent] = useState(false);
  const [host, setHost] = useState<HTMLDivElement | null>(null);

  // A callback ref rather than useRef: the pieces cannot be built until the
  // host is in the document and has a height to measure.
  const hostRef = useCallback((node: HTMLDivElement | null) => setHost(node), []);

  // Keep --region-h on the host in step with its own height, so a piece
  // mid-flight lands where it should even though the certificate frame resizes
  // under it when the image loads.
  useEffect(() => {
    if (!host) return undefined;

    const publish = () =>
      host.style.setProperty(
        "--region-h",
        `${host.offsetHeight || Math.round(window.innerHeight * 0.6)}px`
      );

    publish();

    // The known cause of the region changing size is an image inside it
    // finishing loading -- the certificate frame is a placeholder shape until
    // then. load does not bubble, but it does capture, so one listener on the
    // region catches every image in it.
    const region = host.parentElement;
    region?.addEventListener("load", publish, true);
    window.addEventListener("resize", publish);

    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    observer?.observe(host);

    return () => {
      region?.removeEventListener("load", publish, true);
      window.removeEventListener("resize", publish);
      observer?.disconnect();
    };
  }, [host]);

  useEffect(() => {
    if (prefersReducedMotion() || !host) return undefined;

    const [minTravel, maxTravel] = travel;
    const [minDuration, maxDuration] = duration;
    const count = Math.round(
      window.innerWidth < 600 ? pieceCount * 0.58 : pieceCount
    );

    // The stagger is scaled to the fall rather than fixed, so a quick shot
    // arrives as one burst instead of trickling in over a window longer than
    // the fall itself.
    const maxDelay = minDuration * 0.3;

    setPieces(
      Array.from({ length: count }, (_, index) => ({
        key: index,
        round: Math.random() > 0.6,
        background: colours[Math.floor(Math.random() * colours.length)],
        left: `${Math.random() * 100}%`,
        "--delay": `${(Math.random() * maxDelay).toFixed(2)}s`,
        "--dur": `${(
          minDuration +
          Math.random() * (maxDuration - minDuration)
        ).toFixed(2)}s`,
        "--travel-f": (
          minTravel +
          Math.random() * (maxTravel - minTravel)
        ).toFixed(3),
        "--spin": `${Math.random() * 900 - 450}deg`,
      }))
    );

    const timer = setTimeout(
      () => setSpent(true),
      lifetimeMs(maxDuration, maxDelay)
    );
    return () => clearTimeout(timer);
    // Measured once on mount. Re-running on a resize would restart the shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host]);

  if (spent || prefersReducedMotion()) return null;

  return (
    <div className="cert-confetti" ref={hostRef} aria-hidden="true">
      {pieces.map(({ key, round, background, left, ...vars }) => (
        <i
          key={key}
          className={round ? "round" : ""}
          style={{ background, left, ...vars } as React.CSSProperties}
        />
      ))}
    </div>
  );
};

export default Confetti;
